import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, Input, OnInit, output, signal, viewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BroadcastService } from '@app/common/services/broadcast.service';
import { DeviceManagerService } from '@app/core/services/device-manager.service';
import { filter } from 'rxjs';
import { ICentralUserInfo } from '@app/shared/models';
import { isNavItemActive } from './models/sidebar-nav-active';
import { LocalCacheService } from '@app/common/services/local-cache.service';
import { SidebarNavItem } from './models/sidebar-nav-item.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Tooltip } from 'primeng/tooltip';
import { IconSvgComponent } from '@app/shared/icon-svg/icon-svg.component';
import { IcScrollbarDirective, measureScrollbarGutter } from '@app/shared/directives/ic-scrollbar.directive';
import { ToolbarAccountSelectComponent } from '@app/shared/layout/header/toolbars/toolbar-account-select/toolbar-account-select.component';
import { ToolbarAdminAccountComponent } from '@app/shared/layout/header/toolbars/toolbar-admin-account/toolbar-admin-account.component';
import { Bind } from 'primeng/bind';
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion';
import { Ripple } from 'primeng/ripple';
import { MenuIconsComponent } from '@app/shared/icon-svg/menu-icons/menu-icons.component';
import { TranslatePipe } from '@ngx-translate/core';

export enum ClickableMenuItem {
    NotificationOptions,
    EmailAddresses,
    AccountSelect,
    CreateMainAccount,
    StandardProgramming,
    AutomaticScheduleGenerator,
    FlowManagementIrrigation,
    GlobalAdjustments,
    FlowSettings,
    ProgramsAndStations,
    AutoLearnFlow,
    RetrieveFlow,
    CommunicationTest,
    ControllerConnectivity,
    Shutdown,
    SharingSource,
    WeatherSetup,
    Transactions,
    Transmissions,
    ActivityReport,
    IrrigationScheduleReport,
    WaterUsageReport,
    AlarmReport,
    DailyEtReport,
    ViewPayBalance,
    UpcomingCharges,
    PaymentHistory,
    Phone,
    Email
}

@Component({
    host: { '(window:resize)': 'onResize()' },
    selector: 'ic-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    // OnPush (AB#24384). It hosts ic-toolbar-account-select, whose state is signals since AB#23266 slice 12, so the
    // picker redraws itself; that is the hosting rule in CODING-STANDARDS 5.2. AB#24308 had put it back to Eager while
    // the picker still used plain fields.
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        Tooltip,
        IconSvgComponent,
        ToolbarAccountSelectComponent,
        ToolbarAdminAccountComponent,
        Bind,
        Accordion,
        AccordionPanel,
        Ripple,
        AccordionHeader,
        MenuIconsComponent,
        AccordionContent,
        TranslatePipe,
        IcScrollbarDirective
    ]
})
export class SidebarComponent implements OnInit, AfterViewInit {
    private readonly destroyRef = inject(DestroyRef);

    private broadcastService = inject(BroadcastService);
    private deviceManager = inject(DeviceManagerService);
    private localCacheService = inject(LocalCacheService);
    private router = inject(Router);

    onResize() {
        clearTimeout(this.resizeTimeout);

        // Debounce resize events until done resizing.
        this.resizeTimeout = setTimeout(() => {
            this.setTransformBasedOnScrollbar();
        }, 100);
    }

    private readonly _isSmallDesktop = signal(false);
    get isSmallDesktop(): boolean { return this._isSmallDesktop(); }
    set isSmallDesktop(value: boolean) { this._isSmallDesktop.set(value); }
    private resizeTimeout: any;

    readonly scrollDivRef = viewChild.required<ElementRef<HTMLDivElement>>('scrollDiv');
    private readonly accordion = viewChild.required(Accordion);

    readonly isPinnedChange = output<boolean>();
    readonly isOpenChange = output<boolean>();

    // Inputs the component also writes to, so not signal inputs; each is backed by a signal instead, which is
    // what re-renders an OnPush view without zone.js (ADR 0008, AB#23266).
    private readonly _isSidebarOpen = signal(false);
    @Input() set isSidebarOpen(value: boolean) {
        this._isSidebarOpen.set(value);

        // The open layout (labels, chevrons, footer text) appears as soon as the sidebar starts opening, but stays until
        // it has finished closing, so it is covered by the closing animation instead of vanishing as it starts.
        clearTimeout(this.openLayoutTimeout);
        if (value) {
            this._showOpenLayout.set(true);
        } else {
            this.openLayoutTimeout = setTimeout(() => this._showOpenLayout.set(false), SidebarComponent.SIDEBAR_ANIMATION_MS);
        }
    }
    get isSidebarOpen(): boolean { return this._isSidebarOpen(); }

    private readonly _showOpenLayout = signal(false);
    protected get showOpenLayout(): boolean { return this._showOpenLayout(); }
    private openLayoutTimeout: ReturnType<typeof setTimeout> | undefined;

    private readonly _isPinned = signal(false);
    @Input() set isPinned(value: boolean) { this._isPinned.set(value); }
    get isPinned(): boolean { return this._isPinned(); }

    private readonly _user = signal<ICentralUserInfo | undefined>(undefined);
    @Input() set user(value: ICentralUserInfo | undefined) { this._user.set(value); }
    get user(): ICentralUserInfo | undefined { return this._user(); }

    protected readonly imagePathRoot: string = '../../../../assets/img/side-nav';
    // Matches the 0.3s open/close transition on #sidebar-nav-container.
    private static readonly SIDEBAR_ANIMATION_MS: number = 300;
    private static readonly SIDEBAR_DELAY_FOR_TAB_CLOSE: number = 300;
    private static readonly TAB_OPEN_DELAY_FOR_SIDEBAR_OPEN: number = 200;
    private static readonly DELAY_FOR_MOBILE_SIDEBAR_CLOSE: number = 300;
    private SMALL_DESKTOP_MAX_WIDTH = 1919;
    private SMALL_DESKTOP_MIN_WIDTH = 1440;

    private readonly _pinTooltip = signal('');
    protected get pinTooltip(): string { return this._pinTooltip(); }
    protected set pinTooltip(value: string) { this._pinTooltip.set(value); }

    private readonly _activeIndex = signal<number | null | undefined>(undefined);
    protected get activeIndex(): number | null | undefined { return this._activeIndex(); }
    protected set activeIndex(value: number | null | undefined) { this._activeIndex.set(value); }

    // Replaced, never mutated in place: setActiveTab() assigns a new array after flagging the active tab.
    private readonly _navItems = signal<SidebarNavItem[]>([]);
    protected get navItems(): SidebarNavItem[] { return this._navItems(); }
    protected set navItems(value: SidebarNavItem[]) { this._navItems.set(value); }

    private readonly _showMobileMenu = signal(false);
    protected get showMobileMenu(): boolean { return this._showMobileMenu(); }
    protected set showMobileMenu(value: boolean) { this._showMobileMenu.set(value); }

    private readonly _activeRoute = signal('');
    protected get activeRoute(): string { return this._activeRoute(); }
    protected set activeRoute(value: string) { this._activeRoute.set(value); }

    // Semaphore to track if a Tab is expanded/open.
    // Is used to control timing of tab close in relation to Sidebar closing.
    private isTabExpanded = false;

    ngOnInit(): void {
        this.destroyRef.onDestroy(() => clearTimeout(this.openLayoutTimeout));

        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef))
            .subscribe((event: NavigationEnd) => {
                this.activeRoute = event.urlAfterRedirects;

                this.setActiveTab();
            });

        this.activeRoute = this.router.url;

        this.setupNavItems();

        this.deviceManager.windowResized
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(windowSize => {
                this.isSmallDesktop = windowSize.width <= this.SMALL_DESKTOP_MAX_WIDTH && windowSize.width >= this.SMALL_DESKTOP_MIN_WIDTH;
                if (this.isSmallDesktop) {
                    // Close the sidebar if we are resizing to small desktop and the sidebar is open but not pinned.
                    if (this.isSidebarOpen) {
                        this.toggleSidebarOpen();
                    }
                    this.localCacheService.isSidebarPinned = !this.isSmallDesktop; // To not persist pin state for small desktop.
                }
            });

        const isPinned = this.localCacheService.isSidebarPinned;
        if (isPinned) {
            // Slight delay to avoid ExpressionChangedAfterItHasBeenCheckedError error.
            setTimeout(() => {
                this.toggleSidebarOpen();
                this.toggleSidebarPin();
            });
        } else {
            this.setPinTooltip();
        }

        this.broadcastService.toggleMobileMenu
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                // Sidebar currently closed. Request to open sidebar.
                if (!this.showMobileMenu) {
                    // This may occur if the sidebar is open for desktop and the browser is resized to tablet/mobile.
                    if (this.isSidebarOpen) {
                        this.activeIndex = -1;
                        this.isSidebarOpen = false;
                        this.isTabExpanded = false;
                    }

                    // Open sidebar
                    this.showMobileMenu = !this.showMobileMenu;
                    setTimeout(() => this.toggleSidebarOpen());
                } else {
                    // Close sidebar
                    this.toggleSidebarOpen();

                    // Hide the sidebar
                    // If a tab is expanded, wait for the tab to close plus the time to transition the sidebar.
                    // If no tab is expanded, just wait for the time to transition the sidebar.
                    setTimeout(() => {
                        this.showMobileMenu = !this.showMobileMenu;
                    }, this.isTabExpanded
                        ? SidebarComponent.SIDEBAR_DELAY_FOR_TAB_CLOSE + SidebarComponent.DELAY_FOR_MOBILE_SIDEBAR_CLOSE
                        : SidebarComponent.DELAY_FOR_MOBILE_SIDEBAR_CLOSE);
                }
            });
    }

    ngAfterViewInit() {
        setTimeout(() => this.setTransformBasedOnScrollbar());
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    protected toggleSidebarPin() {
        this.isPinned = !this.isPinned;
        this.isPinnedChange.emit(this.isPinned);
        if (this.isPinned) {
            this.isOpenChange.emit(true);
        }

        this.localCacheService.isSidebarPinned = this.isPinned;
        this.setPinTooltip();
    }

    protected toggleSidebarOpen() {
        if (this.isSidebarOpen && this.activeIndex != -1 && this.activeIndex != null) {
            this.activeIndex = -1;

            /**
             * Allow the expanded tab to close before closing the sidebar.
             * We want the opening and closing of the sidebar to be smooth in relation to smoothly closing any expanded tabs.
             * When a tab is opened, as tracked by this.isTabExpanded, we delay a small number of ms before toggling the sidebar
             * closed. If no tab is expanded, we close immediately.
             */
            setTimeout(() => {
                this.toggleSidebarOpenInternal();
                this.isTabExpanded = false;
            }, this.isTabExpanded ? SidebarComponent.SIDEBAR_DELAY_FOR_TAB_CLOSE : 0);
            return;
        }

        this.toggleSidebarOpenInternal();
    }

    protected onTabClose(event: any) {
        this.activeIndex = -1;
    }

    // Event triggered by the Accordion. PrimeNG 22 emits it before onOpen/onClose, from inside the header's click
    // handler, and only emits onOpen if the tab is still active afterwards. It controls the timing of tab open/close,
    // opens the sidebar for a tab selected from the collapsed rail, and stops tabs with no child nodes expanding at all.
    protected onActiveIndexChange(value: number | string | (number | string)[] | null | undefined) {
        // PrimeNG 22 types valueChange as AccordionValue; this accordion is single-select with
        // numeric panel values, so the runtime value is always a number (or null when closed).
        const index = (value == null || Array.isArray(value)) ? null : Number(value);
        this.isTabExpanded = false;

        if (index === null || index == -1) {
            this.activeIndex = -1;
            return;
        }

        if (!this.isExpandable(index)) {
            // A tab without children never expands: shut it in this same pass, before its (empty) content can start
            // animating, then run its click handler.
            this.closeAccordionPanel();
            setTimeout(() => {
                const navItem = this.navItems[index];
                if (navItem?.onClick != null) {
                    navItem.onClick(navItem);
                }
            });
            return;
        }

        if (!this.isSidebarOpen) {
            // Selected from the collapsed rail: open the sidebar, and hold the panel shut until it has slid open so the
            // tab expands once, afterwards. Letting the accordion open it now would play the expansion while the
            // sidebar is still sliding and again after it.
            this.closeAccordionPanel();
            this.toggleSidebarOpen();
            setTimeout(() => {
                this.activeIndex = index;
                this.isTabExpanded = true;
            }, SidebarComponent.TAB_OPEN_DELAY_FOR_SIDEBAR_OPEN);
            return;
        }

        // The sidebar is already open: the accordion is expanding the tab itself, so just track it.
        this.activeIndex = index;
        this.isTabExpanded = true;
    }

    protected onAccountSelectionMade(accountID: string): void {
        if (this.isSidebarOpen && !this.isPinned) {
            this.toggleSidebarOpen();
        }
    }

    protected onAdminDashboardClicked(): void {
        this.closeOnSelection();
    }

    protected onAdminAccountDetailsClicked(): void {
        this.closeOnSelection();
    }

    protected isActiveTab(route: string): boolean {
        console.log(route);
        return route?.startsWith('/account') || route?.startsWith('/users') || route?.startsWith('/home/');

    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    // Shuts the accordion's own value as well as activeIndex. A header click has already set the accordion's value, and
    // activeIndex may still read -1 from before the click, so resetting activeIndex alone would not reach the accordion.
    // Doing both in the click's own pass means the panel never renders as open.
    private closeAccordionPanel(): void {
        this.activeIndex = -1;
        this.accordion().value.set(-1);
    }

    private isExpandable(index: number): boolean {
        return this.navItems[index].children?.length > 0;
    }

    private toggleSidebarOpenInternal() {
        this.isSidebarOpen = !this.isSidebarOpen;
        this.isOpenChange.emit(this.isSidebarOpen);
        if (this.isPinned) {
            this.isPinned = false;
            this.isPinnedChange.emit(false);

            this.localCacheService.isSidebarPinned = this.isPinned;
            this.setPinTooltip();
        }

        // Reset sidebar semaphore if we are closing as the result of a menu selection.
        if (!this.isSidebarOpen) {
            setTimeout(() => this.showMobileMenu = false, SidebarComponent.DELAY_FOR_MOBILE_SIDEBAR_CLOSE);
        }

        setTimeout(() => this.setTransformBasedOnScrollbar(), 200);
    }

    private setPinTooltip() {
        this.pinTooltip = this.isPinned ? 'Unpin Menu' : 'Pin Menu';
    }

    /**
     * Updates the '--scrollbar-width' CSS variable on the document root to the gutter the styled scrollbar of
     * #sidebar-content takes (measured on the element itself) if a scrollbar is present, otherwise sets it to 0px.
     *
     * This allows SCSS to dynamically adjust layout based on scrollbar presence without layout shifts,
     * ensuring consistent sidebar positioning with or without scrollbars.
     */
    private setTransformBasedOnScrollbar() {
        const el = this.scrollDivRef().nativeElement;
        const scrollbarPresent = el.scrollHeight > el.clientHeight;
        const scrollbarWidth = scrollbarPresent ? measureScrollbarGutter(el) : 0;

        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    }

    // Sets the menu items for the Sidebar

    // For icon use menu-icons.component and icon = ngSwitchCase
    private setupNavItems() {
        this.navItems = [
            new SidebarNavItem({
                label: 'MENUS.HOME',
                icon: 'home',//`${this.imagePathRoot}/sn-home.png`,
                routerLink: '/home',
                onClick: this.onNavigate.bind(this)
            }),
            new SidebarNavItem({
                label: 'MENUS.ACCOUNT',
                icon: 'person-settings',
                isActiveTab: false,
                children: [
                    new SidebarNavItem({
                        label: 'MENUS.EMAIL_SETUP',
                        isActiveTab: false,
                        children: [
                            new SidebarNavItem({
                                label: 'MENUS.NOTIFICATION_OPTIONS',
                                isActiveRoute: this.activeRoute == '/user/notifications',
                                routerLink: '/user/notifications',
                                clickId: ClickableMenuItem.NotificationOptions,
                                onClick: this.onNavigate.bind(this)
                            }),
                            new SidebarNavItem({
                                label: 'MENUS.EMAIL_ADDRESSES',
                                isActiveRoute: this.activeRoute == '/user/preferences',
                                routerLink: '/user/preferences',
                                clickId: ClickableMenuItem.EmailAddresses,
                                onClick: this.onNavigate.bind(this)
                            })
                        ]
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.ACCOUNT_SELECT',
                        isActiveRoute: this.activeRoute === '/account/account-select',
                        routerLink: '/account/account-select',
                        clickId: ClickableMenuItem.AccountSelect,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.CREATE_MAIN_ACCOUNT',
                        isActiveRoute: this.activeRoute === '/account/create-account',
                        routerLink: '/account/create-account',
                        clickId: ClickableMenuItem.CreateMainAccount,
                        onClick: this.onNavigate.bind(this)
                    }),
                ]
            }),
            new SidebarNavItem({
                label: 'MENUS.MAP',
                icon: 'map',
                isActiveRoute: this.activeRoute === '/map-setup',
                routerLink: '/map-setup',
                clickId: ClickableMenuItem.Shutdown,
                onClick: this.onNavigate.bind(this)
            }),
            new SidebarNavItem({
                label: 'MENUS.PROGRAM',
                icon: 'option',
                isActiveTab: false,
                children: [
                    new SidebarNavItem({
                        label: 'MENUS.STANDARD_PROGRAMMING',
                        routerLink: '/program/standard-programming',
                        clickId: ClickableMenuItem.StandardProgramming,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.AUTOMATIC_SCHEDULE_GENERATOR',
                        routerLink: '/program/asg',
                        clickId: ClickableMenuItem.AutomaticScheduleGenerator,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.FLOW_MANAGED_IRRIGATION',
                        routerLink: '/program/fmi',
                        clickId: ClickableMenuItem.FlowManagementIrrigation,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.GLOBAL_ADJUSTMENTS',
                        routerLink: '/program/global-adjustments',
                        clickId: ClickableMenuItem.GlobalAdjustments,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.FLOW_SETTINGS',
                        routerLink: '/program/flow-settings',
                        clickId: ClickableMenuItem.FlowSettings,
                        onClick: this.onNavigate.bind(this)
                    })
                ]
            }),
            new SidebarNavItem({
                label: 'MENUS.MANUAL_OPS',
                icon: 'wrench',
                routerLink: '/manual-ops/program',
                activePrefix: '/manual-ops',
                clickId: ClickableMenuItem.ProgramsAndStations,
                onClick: this.onNavigate.bind(this)
                // One link straight to Programs and Stations (ER-1542). The hidden sub-menu entries below are kept as they were.
                    // new SidebarNavItem({
                    //     label: 'MENUS.COMMUNICATION_TEST',
                    //     routerLink: '/manual-ops/communication',
                    //     clickId: ClickableMenuItem.CommunicationTest,
                    //     onClick: this.onNavigate.bind(this)
                    // }),
                    // new SidebarNavItem({
                    //     label: 'MENUS.CONTROLLER_CONNECTIVITY',
                    //     routerLink: '/manual-ops/controller-connectivity',
                    //     clickId: ClickableMenuItem.ControllerConnectivity,
                    //     onClick: this.onNavigate.bind(this)
                    // })
            }),
            new SidebarNavItem({
                label: 'MENUS.SHUTDOWN',
                icon: 'drop',
                routerLink: '/shutdown',
                clickId: ClickableMenuItem.Shutdown,
                onClick: this.onNavigate.bind(this)
            }),
            new SidebarNavItem({
                label: 'MENUS.WEATHER',
                icon: 'umbrella',
                routerLink: '/weather',
                clickId: ClickableMenuItem.WeatherSetup,
                onClick: this.onNavigate.bind(this)
            }),
            new SidebarNavItem({
                label: 'MENUS.REPORTS',
                icon: 'data-usage-edit',
                isActiveTab: false,
                children: [
                    // new SidebarNavItem({
                    //     label: 'MENUS.COMMUNICATION_HISTORY',
                    //     children: [
                    //         new SidebarNavItem({
                    //             label: 'MENUS.ICENTRAL_TRANSACTIONS',
                    //             routerLink: '/report/icentral',
                    //             clickId: ClickableMenuItem.Transactions,
                    //             onClick: this.onNavigate.bind(this)
                    //         }),
                    //         new SidebarNavItem({
                    //             label: 'MENUS.CONTROLLER_TRANSMISSIONS',
                    //             routerLink: '/reports/controller',
                    //             clickId: ClickableMenuItem.Transmissions,
                    //             onClick: this.onNavigate.bind(this)
                    //         })
                    //     ]
                    // }),
                    new SidebarNavItem({
                        label: 'MENUS.ACTIVITY_REPORT',
                        routerLink: '/report/activity-report',
                        clickId: ClickableMenuItem.ActivityReport,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.IRRIGATION_SCHEDULE_REPORT',
                        routerLink: '/report/irrigation',
                        clickId: ClickableMenuItem.IrrigationScheduleReport,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.WATER_USAGE_REPORT',
                        routerLink: '/reports/water-use',
                        clickId: ClickableMenuItem.WaterUsageReport,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.ALARM_REPORT',
                        routerLink: '/report/alarm-report',
                        clickId: ClickableMenuItem.AlarmReport,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.DAILY_ET_REPORT',
                        routerLink: '/reports/daily-et',
                        clickId: ClickableMenuItem.DailyEtReport,
                        onClick: this.onNavigate.bind(this)
                    })
                ]
            }),
            new SidebarNavItem({
                label: 'MENUS.PAYMENTS',
                icon: 'payment',
                isActiveTab: false,
                children: [
                    new SidebarNavItem({
                        label: 'MENUS.VIEW_PAY_BALANCE',
                        routerLink: '/billing/balance',
                        clickId: ClickableMenuItem.ViewPayBalance,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.UPCOMING_CHARGES',
                        routerLink: '/billing/upcoming',
                        clickId: ClickableMenuItem.UpcomingCharges,
                        onClick: this.onNavigate.bind(this)
                    }),
                    new SidebarNavItem({
                        label: 'MENUS.PAYMENT_HISTORY',
                        routerLink: '/billing/history',
                        clickId: ClickableMenuItem.PaymentHistory,
                        onClick: this.onNavigate.bind(this)
                    })
                ]
            }),
            // Contact is hidden from the side navigation (ER-1542): Help is the contact surface. Kept, not deleted, so it can be restored.
            // new SidebarNavItem({
            //     label: 'MENUS.CONTACT',
            //     icon: 'call',
            //     isActiveTab: false,
            //     children: [
            //         new SidebarNavItem({
            //             label: 'MENUS.PHONE_NO_PREF',
            //             url: 'tel:+18007771477',
            //             clickId: ClickableMenuItem.Phone,
            //             onClick: this.onNavigate.bind(this)
            //         }),
            //         new SidebarNavItem({
            //             label: 'MENUS.EMAIL_NO_PREF',
            //             url: 'mailto:icentral.support@rainmaster.com',
            //             clickId: ClickableMenuItem.Email,
            //             onClick: this.onNavigate.bind(this)
            //         })
            //     ]
            // }),
            new SidebarNavItem({
                label: 'MENUS.HELP',
                icon: 'chat-help',
                routerLink: '/help',
                onClick: this.onNavigate.bind(this)
            }),
        ];

        this.setActiveTab();
    }

    private onNavigate(menuItem: SidebarNavItem): void {
        /**
         * A SidebarNavItem can provide a routerLink to route to a route, an url to open in a new browser tab, or
         * take any arbitrary action. If taking an arbitrary action, the Nav Item must provide a ClickId, which
         * uniquely identifies the Nav Item. A Switch Case should be created for the ClickId.
         * */
        if (menuItem.routerLink) {
            // Navigate to routerLink if provided.
            setTimeout(() => {
                this.router.navigate([menuItem.routerLink]);
            });
        } else if (menuItem.url) {
            // Launch url if Provided.
            window.open(menuItem.url, '_blank', 'noopener,noreferrer');

        } else {
            // Take any arbitrary action on menu item that does not provide a routerLink or Url value.
            switch (menuItem.clickId) {
                // case ClickableMenuItem.NotificationOptions:
                //     // TODO: Do something interesting.
                //     break;

                default:
                    console.error('A click handler for this menu item has not been implemented.');
            }
        }

        setTimeout(() => {
            this.closeOnSelection();
        });
    }

    private closeOnSelection() {
        // Close the sidebar on selection if the sidebar is not pinned.
        if (this.isSidebarOpen && !this.isPinned) {
            this.toggleSidebarOpen();
        }
    }

    private setActiveTab() {

        // A leaf is active through its own route (activePrefix lets Manual Ops light on every /manual-ops page);
        // a group is active when any child or grandchild route matches.
        for (const item of this.navItems) {
            item.isActiveTab = item.children.length
                ? item.children.some(child => child.routerLink
                    ? isNavItemActive(this.activeRoute, child)
                    : child.children.some(grandchild => isNavItemActive(this.activeRoute, grandchild)))
                : isNavItemActive(this.activeRoute, item);
        }

        // The flags above were set on the existing items; a new array is what tells the OnPush view (AB#23266).
        this.navItems = [...this.navItems];
    }
}
